import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {BenjiKIV} from "../../BenjiKIV";
import {SimpleReelModelState} from "../../../core/reel/ReelInterfaces";
import {SimpleReelModelEventData} from "../../reels/BenjiKIVReelModel";
import {ArrayUtils} from "../../utils/ArrayUtils";
import {BenjiKIVSymbol} from "../../reels/BenjiKIVSymbol";
import {BenjiKIVWildSymbol} from "../../reels/BenjiKIVWildSymbol";
import {BenjiKIVStackedSymbol} from "../../reels/BenjiKIVStackedSymbol";
import {gsap} from "gsap";
import {BenjiKIVSound} from "../../sound/BenjiKIVSound";
import {BenjiKIVStatics} from "../../BenjiKIVStatics";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {BenjiKIVGameMode} from "../../server/BenjiKIVGameMode";

/**
 * Created by Jerker Nord on 2023-01-16.
 */

export class BenjiKIVSpinResultRunner implements SimplePresentation {

    protected _tl:gsap.core.Timeline;
    protected _stoppedReels:boolean[];
    protected _reelsReadyToStop:boolean[];
    protected _controller:SimplePresentationRunnerController;
    protected _reelStoppedAnimations:gsap.core.Timeline[];
    protected _emptyTL:gsap.core.Timeline = gsap.timeline();

    private _spinAborted:boolean;
    private _scatterCounter:number;
    private _scattersLandedInSpinCounter:number;
    private _lastReelDelayTL:gsap.core.Timeline;

    public shouldRun():boolean {
        return true;
    }

    public start(controller:SimplePresentationRunnerController):void {
        this._controller = controller;
        this._spinAborted = false;
        BenjiKIVStatics.isSpinAborted = false;
        this._scatterCounter = BenjiKIV.overlayScatterView.activeOverlayScatters.length;
        this._scattersLandedInSpinCounter = 0;
        this._reelsReadyToStop = [false, false, false, false, false];
        for(let i:number = 0; i < BenjiKIV.model.gameData.reels.length; i++) {
            BenjiKIV.allReelView.reelModels[i].events?.once(SimpleReelModelState.READY_TO_STOP, (data:SimpleReelModelEventData) => this.onReelReadyToStop(data));
            BenjiKIV.allReelView.reelModels[i].getReadyToStop();
        }
    }

    private onReelReadyToStop(data:SimpleReelModelEventData) {
        this._reelsReadyToStop[data.reel] = true;
        if(ArrayUtils.isAllTrue(this._reelsReadyToStop)) {
            this.doStop();
        }
    }

    protected doStop():void {
        this._stoppedReels = [false, false, false, false, false];
        this._reelStoppedAnimations = [this._emptyTL, this._emptyTL, this._emptyTL, this._emptyTL, this._emptyTL];
        const isFastSpin:boolean = BenjiKIV.apiPlugin.settings.get(APISetting.FAST_SPIN, false);

        this._tl = gsap.timeline();
        this._tl.add(() => this._controller.setSkipCallback(() => this.skip()));

        for(let i:number = 0; i < BenjiKIV.model.gameData.reels.length; i++) {
            let stopDelay:number = isFastSpin ? BenjiKIVStatics.reelStopDelayFastSpin[i] : BenjiKIVStatics.reelStopDelayNormal[i];
            const reelData:string[] = BenjiKIV.model.gameData.reels[i];
            const reelStopAnimation:gsap.core.Timeline = gsap.timeline();

            if(BenjiKIV.model.badOutcomeReels[i] == true) {
                stopDelay = isFastSpin ? BenjiKIVStatics.reelStopDelayBadOutcomeFast[i] : BenjiKIVStatics.reelStopDelayBadOutcomeNormal[i];
            }

            if(BenjiKIV.model.goodOutcomeReels[i] == true) {
                stopDelay += 0.3;
            }

            if(BenjiKIV.model.diceAnticipationReels[i] == true) {
                if(BenjiKIV.model.isLuckyDrawBet == true) {
                    stopDelay += BenjiKIVStatics.DICE_ANTICIPATION_DELAY_LUCKY_DRAW;
                } else {
                    stopDelay += isFastSpin ? BenjiKIVStatics.DICE_ANTICIPATION_DELAY_FAST : BenjiKIVStatics.DICE_ANTICIPATION_DELAY_NORMAL;
                }
            }

            if(BenjiKIV.model.deadPresidentAnticipationReels[i] == true && BenjiKIV.model.diceAnticipationReels[i] == false) {
                stopDelay += isFastSpin ? BenjiKIVStatics.DEAD_ANTICIPATION_DELAY_FAST : BenjiKIVStatics.DEAD_ANTICIPATION_DELAY_NORMAL;
            }

            //prevents useless skips when last reel has started its stop anyway.
            if(i == 4) {
                reelStopAnimation.add(() => {this._controller.setSkipCallback()});
            }

            //Do show enhancer view result + ring girl
            reelStopAnimation.add(this.getRingGirlAndResultTL(i));

            reelStopAnimation.add(() => BenjiKIV.allReelView.reelModels[i].doStop(reelData), "+=" + stopDelay);

            //Create split reel animation (if any)
            if(BenjiKIV.model.data.enhancerReel[i] == 1) {
                this._reelStoppedAnimations[i] = this.doSplitReel(i);
            }

            //Add stop spin delay if previous reel has a reel stopped animation
            if(i > 0) {
                if(this._reelStoppedAnimations[i - 1].totalDuration() > 0) {
                    reelStopAnimation.delay(stopDelay + this._reelStoppedAnimations[i - 1].totalDuration() + 0.5);
                }
            }

            BenjiKIV.allReelView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data:SimpleReelModelEventData) => this.onReelStop(data, this._controller));
            BenjiKIV.allReelView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data:SimpleReelModelEventData) => this.onReelBounce(data));

            this._tl.add(reelStopAnimation);
        }
    }

    protected doSplitReel(reelId:number):gsap.core.Timeline {
        let playSliceSound:boolean = true;
        const mainSplitReelTL:gsap.core.Timeline = gsap.timeline({paused : true});
        mainSplitReelTL.add(gsap.timeline({delay : 0.2}));
        const splitSymbolTLs:gsap.core.Timeline[] = [];
        let lowSymSplitSpeed:number = 1;
        mainSplitReelTL.add(() => {
            for(let i:number = 2; i < 7; i++) {
                const splitSymbolTL:gsap.core.Timeline = gsap.timeline();
                let symbol:BenjiKIVSymbol = (<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[reelId].reelView.symbols[i]);
                //Only check for split lows in visible area
                if(symbol.symbolName.indexOf("L") > -1 && i > 2 && i < 6) {
                    if(playSliceSound) {
                        BenjiKIV.soundPlayer.playSound(BenjiKIVSound.XSPLIT_SLICE);
                        playSliceSound = false;
                    }
                    if(this._spinAborted == true) {
                        lowSymSplitSpeed = 3;
                    }
                    splitSymbolTL.add(BenjiKIV.splitSymbolView.getSplitSymbolAnimation(symbol.reelId, symbol.reelPosition).timeScale(lowSymSplitSpeed));
                    splitSymbolTL.add(symbol.setSplitLevel(2), "-=0.25");
                } else if(BenjiKIV.allReelView.reelModels[reelId].reelView.symbols[i].isStacked == true) {
                    if(BenjiKIV.allReelView.reelModels[reelId].reelView.symbols[i].stackedSymbolName.indexOf("W_MID") > -1) {
                        splitSymbolTL.add(() => (<BenjiKIVWildSymbol>symbol).updateMultiContainerPosition());
                        splitSymbolTL.add([
                            () => {BenjiKIV.soundPlayer.playSound(BenjiKIVSound.MULTIPLIER_COUNT_UP.next())},
                            (<BenjiKIVWildSymbol>symbol).updateMultiplier(2)
                        ]);
                    } else if(BenjiKIV.allReelView.reelModels[reelId].reelView.symbols[i].stackedSymbolName.indexOf("_MID") > -1) {
                        splitSymbolTL.add(() => (<BenjiKIVStackedSymbol>symbol).updateMultiContainerPosition());
                        splitSymbolTL.add([
                            () => {BenjiKIV.soundPlayer.playSound(BenjiKIVSound.MULTIPLIER_COUNT_UP.next())},
                            (<BenjiKIVStackedSymbol>symbol).updateMultiplier(2)
                        ]);
                    }
                }
                splitSymbolTLs.push(splitSymbolTL);
            }
        })
        mainSplitReelTL.add(splitSymbolTLs);

        mainSplitReelTL.add(() => {
            if(BenjiKIV.model.isXBetRedSpecialMode == true && BenjiKIV.model.scattersLanded[reelId] !== -1) {
                //Do nothing?
            } else {
                BenjiKIV.enhancerView.activateDarkOverlay(reelId);
            }
        });

        return mainSplitReelTL;
    }

    protected getRingGirlAndResultTL(reelId:number):gsap.core.Timeline {
        const resultTL:gsap.core.Timeline = gsap.timeline();

        if(BenjiKIV.model.showRingGirlOnReel[reelId] == true && BenjiKIV.model.data.enhancerReel[reelId] == 1) {
            resultTL.add(BenjiKIV.enhancerView.getRingGirlShowSplitAnimation(reelId));
            resultTL.add(BenjiKIV.enhancerView.xSplitReelAttentionOn(reelId), "-=1.1");
        }
        resultTL.add(() => BenjiKIV.allReelView.reelModels[reelId].reelView.deActivateDarkReel());

        return resultTL;
    }

    protected skip():void {
        BenjiKIV.enhancerView.isSpinResultAborted = true;
        BenjiKIVStatics.isSpinAborted = true;
        this._controller.setSkipCallback();
        this._spinAborted = true;
        this._tl.totalProgress(1);
    }

    protected onReelBounce(data:SimpleReelModelEventData) {
        if(this._spinAborted == false || (this._spinAborted == true && (data.reel == 0 || data.reel == 2 || data.reel == 4))) {
            BenjiKIV.soundPlayer.playSound(BenjiKIVSound.REELS_LANDING[data.reel]);
        }
        if(BenjiKIV.model.wildLandedOnReels[data.reel] == true) {
            BenjiKIV.soundPlayer.playSound(BenjiKIVSound.WILD_LANDING);
        }
        if(BenjiKIV.model.wildLandedFullStackedReels[data.reel] == true) {
            BenjiKIV.wildSymbolsPool.getWildSymbolOnReel(data.reel).nudgeLevel = 2;
        }
        if(BenjiKIV.model.scattersLanded[data.reel] > -1) {
            if(this._scatterCounter > 0) {
                BenjiKIV.scatterPool.spinAllActiveScatters();
            }
            if(this._scatterCounter < 2) {
                this._scattersLandedInSpinCounter++;
                if(this._scattersLandedInSpinCounter == 2) {
                    BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_ANTICIPATION);
                }
            }
            if(BenjiKIV.model.data.mode == BenjiKIVGameMode.FREESPIN || BenjiKIV.model.data.mode == BenjiKIVGameMode.SUPER_FREESPIN) {
                BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_LAND_FS);
            } else {
                BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_LAND_SOUNDS[this._scatterCounter]);
            }
            this._scatterCounter++;
        }
        if(data.reel == 4 && BenjiKIV.model.diceAnticipationReels[data.reel] == true) {
            BenjiKIV.anticipationView.getHideDiceTL(data.reel).restart();
        }

        if(data.reel == 4 && BenjiKIV.model.deadPresidentAnticipationReels[data.reel] == true && BenjiKIV.model.diceAnticipationReels[data.reel] == false) {
            BenjiKIV.anticipationView.getHideDeadTL(data.reel).restart();
        }

    }

    protected onReelStop(data:SimpleReelModelEventData, controller:SimplePresentationRunnerController) {
        if(BenjiKIV.model.data.enhancerReel && BenjiKIV.model.data.enhancerReel[data.reel] == 1) {
            BenjiKIV.enhancerView.xReelAttentionOff(data.reel);
        }

        //Do scatter dice anticipation
        if(data.reel < 4) {
            if(BenjiKIV.model.diceAnticipationReels[data.reel + 1] == true) {
                BenjiKIV.anticipationView.getShowDiceTL(data.reel + 1).restart();
                if(this._spinAborted == false) {
                    BenjiKIV.soundPlayer.playSound(BenjiKIVSound.DICE_ANTICIPATION);
                }
            }
        }
        if(BenjiKIV.model.diceAnticipationReels[data.reel] == true) {
            if(BenjiKIV.anticipationView.getHideDiceTL(data.reel).isActive() == false) {
                BenjiKIV.anticipationView.getHideDiceTL(data.reel).restart();
            }
        }

        //Do dead presidents anticipation
        if(data.reel < 4) {
            if(BenjiKIV.model.deadPresidentAnticipationReels[data.reel + 1] == true && BenjiKIV.model.diceAnticipationReels[data.reel + 1] == false) {
                BenjiKIV.anticipationView.getShowDeadTL(data.reel + 1).restart();
                if(this._spinAborted == false) {
                    BenjiKIV.soundPlayer.playSound(BenjiKIVSound.DEAD_ANTICIPATION);
                }
            }
        }
        if(BenjiKIV.model.deadPresidentAnticipationReels[data.reel] == true && BenjiKIV.model.diceAnticipationReels[data.reel] == false) {
            if(BenjiKIV.anticipationView.getHideDeadTL(data.reel).isActive() == false) {
                BenjiKIV.anticipationView.getHideDeadTL(data.reel).restart();
            }
        }

        if(data.reel == 4) {
            let counter:number = 0;
            for(let i:number = 0; i < BenjiKIV.model.scattersLanded.length; i++) {
                if(BenjiKIV.model.scattersLanded[i] > -1) {
                    if(counter == 0) {
                        const allScatters:number = BenjiKIV.overlayScatterView.activeOverlayScatters.length + BenjiKIV.model.allScattersLanded;
                        if(allScatters > 1) {
                            BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_REVEAL);
                            BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_WIN);
                        }
                        if(allScatters > 2) {
                            BenjiKIV.soundPlayer.playSound(BenjiKIVSound.SCATTER_WIN_FS);
                        }
                    }
                    BenjiKIV.scatterPool.getScatterSymbolOnReel(i).setDiceValue((BenjiKIV.overlayScatterView.activeOverlayScatters.length + 2) + counter);
                    counter++;
                }
            }

            BenjiKIV.soundPlayer.stopSound(BenjiKIVSound.REELS_SPINNING);
            BenjiKIV.soundPlayer.stopSound(BenjiKIVSound.SCATTER_ANTICIPATION);
        }

        //Runs split TL on reel (if any)
        this._reelStoppedAnimations[data.reel].restart();
        //If reel stopped animation on last reel, add small delay to give it time to finish
        if(data.reel == 4 && this._reelStoppedAnimations[data.reel].totalDuration() > 0) {
            this._lastReelDelayTL = gsap.timeline({delay : 0.8});
            this._lastReelDelayTL.add(() => {
                this._stoppedReels[data.reel] = true;
                if(ArrayUtils.isAllTrue(this._stoppedReels)) {
                    this.completePresentation(this._controller);
                }
            });
        } else {
            this._stoppedReels[data.reel] = true;
            if(ArrayUtils.isAllTrue(this._stoppedReels)) {
                this.completePresentation(controller);
            }
        }
    }

    private completePresentation(controller:SimplePresentationRunnerController) {
        controller.finish();
    }
}